import { findTransportLocation } from "@elit-hair/elitklinik-shared";
import { Alert, Avatar, Skeleton } from "@mui/material";
import { grey } from "@mui/material/colors";
import { TFunction } from "i18next";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { TbArrowMoveRight, TbUser } from "react-icons/tb";

import { PassengerTransferTypeLabel } from "@/constants/passengerTransferTypes";
import { IPassengerHistoryCompanionList, companionSchema } from "@/schemas/passenger-history.schemas";
import { PlanEditableFields } from "@/stores/use-plan-table";

const fieldFormatters: {
    [key in keyof PlanEditableFields]: (value: unknown, t: TFunction) => string;
} = {
    origin: (value) => (typeof value === "string" && findTransportLocation(value)?.label) || "",
    destination: (value) => (typeof value === "string" && findTransportLocation(value)?.label) || "",
    transferType: (value) =>
        (typeof value === "number" &&
            value in PassengerTransferTypeLabel &&
            PassengerTransferTypeLabel[value as unknown as keyof typeof PassengerTransferTypeLabel]) ||
        "",
    status: (value, t) => (typeof value === "string" && t("passengerStatuses." + (value || ""))) || "",
    cancelled: (value) => (typeof value === "boolean" && value ? "İptal Edildi" : "İptali Kaldırıldı"),
};

export function HistoryChangeItemFormatter({ value, field }: { value: unknown; field: keyof PlanEditableFields }) {
    const { t } = useTranslation();

    const formatter = fieldFormatters[field];
    if (!value && field !== "cancelled") return <span>-</span>;
    if (formatter) return <span>{formatter(value, t)}</span>;
    const isArray = Array.isArray(value); // it should be companions
    if (isArray) return <span>-</span>;
    const time = DateTime.fromISO(value as string);
    if (time.isValid) return time.setLocale("tr").toFormat("dd.MM.yyyy HH:mm");

    return <span>{String(value)}</span>;
}

function HistoryChangeCompanionItemSkeleton() {
    return (
        <div className="flex items-center gap-2">
            <Skeleton animation="wave" width={14} height={20} variant="text" />

            <Skeleton animation="wave" width={24} height={24} variant="circular" />
            <div className="flex flex-col">
                <Skeleton animation="wave" width={110} height={22} variant="text" />
            </div>
        </div>
    );
}

function HistoryChangeCompanionItem({ companion, index }: { index: number; companion: unknown }) {
    if (!companion || typeof companion !== "object") return null;
    const isValidCompanion = companionSchema.safeParse(companion);
    if (!isValidCompanion.success) return null;
    const { name, phone } = isValidCompanion.data;
    return (
        <>
            <div className="flex items-center gap-2">
                <div>{index + 1}</div>
                <Avatar className="!size-6">
                    <TbUser fontSize={14} />
                </Avatar>
                <div className="flex flex-col">
                    <span className="font-medium  ">{name}</span>
                    <span className="text-gray-400  italic">{phone}</span>
                </div>
            </div>
        </>
    );
}

export function HistoryChangeItemCompanionsSkeleton() {
    return (
        <div className=" flex flex-col w-full col-span-full ">
            <div className="text-gray-500">
                <Skeleton animation="wave" width={100} height={22} variant="text" />
            </div>
            <div className="grid grid-cols-[1fr,70px,1fr] w-full">
                <div className="flex flex-col gap-2">
                    {Array.from({ length: 1 }).map((_, index) => (
                        <HistoryChangeCompanionItemSkeleton key={index} />
                    ))}
                </div>
                <span className="flex items-center justify-center">
                    <TbArrowMoveRight color={grey[500]} fontSize={24} />
                </span>
                <div className="flex flex-col gap-2">
                    {Array.from({ length: 2 }).map((_, index) => (
                        <HistoryChangeCompanionItemSkeleton key={index} />
                    ))}
                </div>
                <div></div>
            </div>
        </div>
    );
}
export function HistoryChangeItemCompanions({
    from,
    to,
    field,
}: {
    from: IPassengerHistoryCompanionList;
    to: IPassengerHistoryCompanionList;
    field: string;
}) {
    const { t } = useTranslation();
    if (!from && !to) return null;

    return (
        <div className=" flex flex-col w-full col-span-full ">
            <div className="text-gray-500">{t(field || "")}</div>
            <div className="grid grid-cols-[1fr,70px,1fr] w-full">
                <div className="flex flex-col gap-2">
                    {from && from.length ? (
                        from.map((item, index) => (
                            <HistoryChangeCompanionItem key={index} index={index} companion={item} />
                        ))
                    ) : (
                        <Alert severity="info" className="!max-w-fit">
                            Refakatçi Yok
                        </Alert>
                    )}
                </div>
                <span className="flex items-center justify-center">
                    <TbArrowMoveRight fontSize={24} />
                </span>
                <div className="flex flex-col gap-2">
                    {to &&
                        to.map((item, index) => (
                            <HistoryChangeCompanionItem key={index} index={index} companion={item} />
                        ))}
                </div>
                <div></div>
            </div>
        </div>
    );
}
